import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { config } from '~/config';
import { useRequest } from '~/lib/useRequest';
import { EntityHistoryEntry, getAccountTypeDisplayName } from './useEntityHistory';

/** Returns the entry with the latest updatedAt; used to merge educationHistory entity history with application history. */
function getMostRecentEntry(
  a: EntityHistoryEntry | undefined | null,
  b: EntityHistoryEntry | undefined | null
): EntityHistoryEntry | undefined {
  const valid = [a, b].filter((e): e is EntityHistoryEntry => Boolean(e?.updatedAt));
  if (valid.length === 0) return undefined;
  return valid.reduce(
    (latest, current) => (new Date(current.updatedAt) > new Date(latest.updatedAt) ? current : latest),
    valid[0]
  );
}

type GetEntityHistoriesResponse = {
  data: {
    id: string;
    type: 'history';
    attributes: {
      referenceId: string;
      entityType: string;
      profileId: string;
      accountType?: string | null;
      changes: Record<string, { old: string; new: string }>;
    };
    meta: {
      createdAt: string;
      updatedAt: string;
    };
  }[];
};

export type EducationHistoryTimestamps = Record<string, EntityHistoryEntry>;

/**
 * Fetches entity history for multiple education history document IDs in parallel.
 * Merges with application history (referenceId 'educationHistory') when provided:
 * for each document, returns the more recent of the doc-level entry and the application-level entry.
 * Result: { documentId: { updatedAt, updatedByRole } }.
 */
export function useEducationHistoryTimestamps(
  docIds: string[],
  applicationEducationHistoryEntry?: EntityHistoryEntry | null
) {
  const request = useRequest();

  const queries = useQueries({
    queries: docIds.map((docId) => ({
      queryKey: ['entity-history', 'educationHistory', docId],
      queryFn: async (): Promise<EntityHistoryEntry | undefined> => {
        const url = `${config.apiHost}/private/v1/entity-histories/educationHistory/${docId}`;
        const response = await request<GetEntityHistoriesResponse>(url, {
          method: 'GET',
          isExpectedResponse: (res): res is GetEntityHistoriesResponse =>
            Boolean(res && typeof res === 'object' && 'data' in res),
        });

        // Find the most recent record with referenceId 'educationHistory'
        let mostRecent: EntityHistoryEntry | undefined;
        response.data.forEach((record) => {
          if (record.attributes.referenceId !== 'educationHistory') return;
          const updatedAt = record.meta.updatedAt;
          if (!mostRecent || new Date(updatedAt) > new Date(mostRecent.updatedAt)) {
            mostRecent = {
              updatedAt,
              updatedByRole: getAccountTypeDisplayName(record.attributes.accountType ?? undefined),
            };
          }
        });

        return mostRecent;
      },
      enabled: Boolean(docId),
    })),
  });

  const queryData = queries.map((q) => q.data);

  const timestamps = useMemo(() => {
    const map: EducationHistoryTimestamps = {};
    queryData.forEach((data, index) => {
      const merged = getMostRecentEntry(data, applicationEducationHistoryEntry ?? undefined);
      if (merged) {
        map[docIds[index]] = merged;
      }
    });
    return map;
  }, [queryData, docIds, applicationEducationHistoryEntry]);

  return { data: timestamps };
}
